import { Delta } from 'jsondiffpatch';
import { Difference } from '@/utils/diff/Difference';

/**
 * Converts a `jsondiffpatch` delta object into a structured list of `MicrodiffEntry`s.
 * ---
 * ### Example of a full delta (as returned by `jsondiffpatch.diff`)
 * {
 *   "addedProperty": ["newValue"],
 *   "modifiedProperty": ["oldValue", "newValue"],
 *   "deletedProperty": ["oldValue", 0, 0],
 *   "nestedObject": {
 *     "childProperty": ["oldChild", "newChild"]
 *   },
 *   "arrayProperty": {
 *     "_t": "a",
 *     "0": ["addedItem"],           // item added at index 0
 *     "_1": ["removedItem", 0, 0],  // item removed from original index 1
 *     "_2": ["", 0, 3]              // item moved (magic number 3)
 *   },
 *   "textProperty": ["@@ -1,7 +1,7 @@\n Hello\n-World\n+Universe", 0, 2]
 * }
 *
 * ---
 * ### Notes
 * - Array deltas use `_t: "a"` to mark array mode.
 * - Indices prefixed with `_` refer to the original array (left side).
 * - When possible, `id` fields are used to key diff entries instead of array indices.
 * - Nested object diffs inside arrays are flattened into field-level `CHANGE` entries.

 * @see https://github.com/benjamine/jsondiffpatch
 */

export class JsondiffpatchConverter {
  private readonly idKey;

  constructor(
    private readonly oldRoot: any,
    private readonly newRoot: any
  ) {
    this.idKey = 'id';
  }

  public convert(delta: Delta): Difference[] {
    const out: Difference[] = [];
    this.walk(delta, [], out);
    return out;
  }

  private walk(node: any, path: string[], out: Difference[]) {
    if (!node || typeof node !== 'object') return;

    if (node._t === 'a') {
      this.handleArray(node, path, out);
      return;
    }

    for (const [key, val] of Object.entries(node)) {
      if (Array.isArray(val)) {
        this.handleScalarDelta(val, [...path, key], out);
      } else if (val && typeof val === 'object') {
        this.walk(val, [...path, key], out);
      }
    }
  }

  private handleScalarDelta(delta: any[], path: string[], out: Difference[]) {
    // [new] -> create
    if (delta.length === 1) {
      out.push({ path, type: 'CREATE', value: delta[0] });
      return;
    }
    // [old, new] -> change
    if (delta.length === 2) {
      out.push({ path, type: 'CHANGE', oldValue: delta[0], value: delta[1] });
      return;
    }
    // [old, 0, 0] -> remove
    if (delta.length === 3 && delta[1] === 0 && delta[2] === 0) {
      out.push({ path, type: 'REMOVE', oldValue: delta[0] });
      return;
    }
    // [unidiff, 0, 2] -> text diff; treat as change of whole string
    if (delta.length === 3 && delta[2] === 2) {
      out.push({ path, type: 'CHANGE', value: delta[0] });
      return;
    }
  }

  private handleArray(
    delta: Record<string, any>,
    path: string[],
    out: Difference[]
  ) {
    for (const [key, val] of Object.entries(delta)) {
      if (key === '_t') continue;

      const isRemovedOrMoved = key.startsWith('_');

      if (isRemovedOrMoved) {
        const fromIndex = Number(key.slice(1));

        if (Array.isArray(val) && val.length === 3 && val[2] === 3) {
          const toIndex = val[1];
          continue;
        }

        // Regular removal
        const oldArr = this.getArrayAtFlexible(this.oldRoot, path);
        const oldItem =
          Array.isArray(val) && val.length ? val[0] : oldArr?.[fromIndex];
        const id = this.readId(oldItem) ?? String(fromIndex);
        out.push({ path: [...path, id], type: 'REMOVE', oldValue: oldItem });
        continue;
      }

      // Additions or nested modifications at final array (right) index
      if (Array.isArray(val)) {
        if (val.length === 1) {
          const added = val[0];
          const id = this.readId(added) ?? String(key);
          out.push({ path: [...path, id], type: 'CREATE', value: added });
        } else if (val.length === 2) {
          out.push({
            path: [...path, key],
            type: 'CHANGE',
            oldValue: val[0],
            value: val[1],
          });
        } else if (val.length === 3 && val[1] === 0 && val[2] === 0) {
          const oldArr = this.getArrayAtFlexible(this.oldRoot, path);
          const oldItem = val[0] ?? oldArr?.[Number(key)];
          const id = this.readId(oldItem) ?? String(key);
          out.push({ path: [...path, id], type: 'REMOVE', oldValue: oldItem });
        }
        continue;
      }

      // Nested object change inside array item
      if (typeof val === 'object') {
        const rightIndex = Number(key);
        const newArr = this.getArrayAtFlexible(this.newRoot, path);
        const newItem = newArr?.[rightIndex];
        const id = this.readId(newItem) ?? String(rightIndex);

        const newValues: Record<string, any> = {};
        const oldValues: Record<string, any> = {};

        for (const [field, change] of Object.entries(val)) {
          if (Array.isArray(change)) {
            if (change.length === 1) newValues[field] = change[0];
            else if (change.length === 2) {
              oldValues[field] = change[0];
              newValues[field] = change[1];
            } else if (
              change.length === 3 &&
              change[1] === 0 &&
              change[2] === 0
            )
              oldValues[field] = change[0];
          } else if (typeof change === 'object') {
            this.walk(change, [...path, id, field], out);
          }
        }

        if (Object.keys(newValues).length || Object.keys(oldValues).length) {
          out.push({
            path: [...path, id],
            type: 'CHANGE',
            value: newValues,
            oldValue: oldValues,
          });
        }
      }
    }
  }

  // ----- utils -----

  private readId(item: any): string | undefined {
    if (item && typeof item === 'object' && this.idKey in item) {
      const v = item[this.idKey];
      return typeof v === 'string' && v.length ? v : undefined;
    }
    return undefined;
  }

  private getAtFlexible(root: any, path: (string | number)[]): any {
    let node = root;
    for (const seg of path) {
      if (node == null) return undefined;

      if (Array.isArray(node)) {
        if (typeof seg === 'number' || /^\d+$/.test(String(seg))) {
          node = node[Number(seg)];
        } else {
          const id = String(seg);
          node = node.find(
            (x: any) => x && typeof x === 'object' && x[this.idKey] === id
          );
        }
      } else {
        node = node[seg as any];
      }
    }
    return node;
  }

  private getArrayAtFlexible(
    root: any,
    path: (string | number)[]
  ): any[] | undefined {
    const node = this.getAtFlexible(root, path);
    return Array.isArray(node) ? node : undefined;
  }
}
